import cn from 'classnames';
import { useState, useRef, useEffect } from 'react';
import { connect } from 'react-redux';

import { addReview, updateReview } from 'actions/reviews';
import ErrorMessage from 'components/common/ErrorMessage';
import BodyModal from 'components/common/modals/BodyModal';
import StarRating from 'components/common/StarRating';
import CloseX from 'components/icons/navigation/CloseX';
import useAnalyticsCallback from 'hooks/useAnalyticsCallback';
import { REVIEW_REASON_PREFIX } from 'lib/constants';
import styles from 'styles/components/common/modals/DeliveredMealReviewModal.module.scss';
import * as Types from 'utilities/types';

const HIGH_RATING = 4;
const LOW_RATING = 2;
const HIGH_RATING_MESSAGE = 'We will recommend this meal more often to you in the future!';
const LOW_RATING_MESSAGE = 'We will recommend this meal less often to you in the future.';

const DeliveredMealReviewModal = ({
  isOpen,
  onClose,
  mealId,
  menuId,
  existingReview,
  onAddReview,
  onUpdateReview,
  onShowToast,
  title,
  reviewSubmittedEvent,
  ratingSubmittedEvent,
}) => {
  const [rating, setRating] = useState(null);
  const [comment, setComment] = useState(null);
  const [error, setError] = useState(null);
  const closeRef = useRef();
  const {
    recipeRating,
    comment: existingComment,
    pendingChanges,
    id: existingReviewId,
  } = existingReview || {};

  const onSubmitReview = useAnalyticsCallback(
    () => handleSubmit(),
    ({ logEvent }) => {
      if (reviewSubmittedEvent) {
        logEvent(reviewSubmittedEvent, {
          mealTitle: title,
          review: comment,
        });
      }
    }
  );

  // Review reason prefix related checks needed if a user was in the tempo tagged review
  // experiment, and was then removed from the experiment
  const getCommentWithoutReviewReasonPrefix = () => {
    if (!existingComment) return '';

    if (existingComment.includes(REVIEW_REASON_PREFIX)) {
      return existingComment.split(REVIEW_REASON_PREFIX).filter(Boolean)[0].trim();
    }
    return existingComment;
  };

  const existingCommentWithoutReviewReasonPrefix = getCommentWithoutReviewReasonPrefix();

  useEffect(() => {
    if (isOpen) {
      if (recipeRating !== null) {
        setRating(recipeRating);
        if (comment === null) {
          setComment(existingCommentWithoutReviewReasonPrefix);
        }
      } else if (pendingChanges?.recipeRating && rating) {
        setRating(pendingChanges.recipeRating);
        saveRating(pendingChanges.recipeRating);
      }
    }
  }, [isOpen, existingReview]);

  const handleRatingChange = newRating => {
    if (newRating === undefined || newRating === null) return;

    const ratingValue = parseInt(newRating, 10);
    if (isNaN(ratingValue)) return;

    setRating(ratingValue);
    saveRating(ratingValue, true);
  };

  const saveRating = async (ratingToSave, keepCurrentComment = false) => {
    if (ratingToSave === null) {
      return;
    }

    const commentToSave = keepCurrentComment ? existingComment : comment;

    try {
      if (existingReviewId) {
        await onUpdateReview(existingReviewId, {
          recipeRating: ratingToSave,
          comment: commentToSave,
          mealId,
          menuId,
        });
      } else {
        await onAddReview({
          recipeRating: ratingToSave,
          comment: commentToSave,
          mealId,
          menuId,
        });
      }
    } catch {
      setError('Unable to save your rating. Please try again.');
    }
  };

  const handleSubmit = async () => {
    if (!rating) return;

    try {
      if (existingReviewId) {
        await onUpdateReview(existingReviewId, {
          recipeRating: rating,
          comment,
          mealId,
          menuId,
        });
      } else {
        await onAddReview({
          recipeRating: rating,
          comment,
          mealId,
          menuId,
        });
      }
      onClose();
      onShowToast();
    } catch {
      setError('Unable to submit your review. Please try again.');
    }
  };

  const getRatingMessage = rating => {
    if (rating >= HIGH_RATING) {
      return HIGH_RATING_MESSAGE;
    }
    if (rating <= LOW_RATING) {
      return LOW_RATING_MESSAGE;
    }
    return null;
  };

  const submitDisabled =
    !rating ||
    !comment?.trim() ||
    (existingReview && comment === existingCommentWithoutReviewReasonPrefix);

  if (!isOpen) return null;

  return (
    <BodyModal
      onDismiss={onClose}
      topBarStyle="hidden"
      titleStyle="hidden"
      bodyPadding="p-0"
      containerStyle="w-full max-w-[360px]"
      contentClassname="rounded-lg"
      isMinHeight={true}
    >
      <div className="flex flex-col rounded-lg bg-white-100 p-2">
        <div className="flex justify-end">
          <button
            ref={closeRef}
            className="text-charcoal-90 transition-colors hover:text-charcoal-100"
            onClick={onClose}
          >
            <span className="sr-only">Close review modal for {title}</span>
            <CloseX className="h-6 w-6" />
          </button>
        </div>

        <div className="flex flex-col px-6 pb-6">
          {error && <ErrorMessage message={error} />}
          <div className="mb-2 text-center">
            <h2 className="text mb-2">
              What did you think of the
              <br />
              <span className="textMd">{title}</span>?
            </h2>
          </div>

          <div className="mb-2 flex justify-center">
            <StarRating
              uniqueId={`meal-review-${mealId}`}
              onChange={handleRatingChange}
              rating={rating}
              disabled={false}
              starSvgClassName="h-[29px] w-[29px]"
              customOutlineColor="text-charcoal-100"
              ratingSubmittedEvent={ratingSubmittedEvent}
            />
          </div>

          <div className="mx-auto w-[280px] pb-2">
            {getRatingMessage(rating) && (
              <p className="text text-center text-charcoal-100">{getRatingMessage(rating)}</p>
            )}
          </div>

          <textarea
            value={comment === null ? '' : comment}
            onChange={e => setComment(e.target.value)}
            className={cn(
              styles.textarea_placeholder,
              'mb-4 w-full flex-grow resize-none rounded border border-charcoal-80 bg-white-100 p-4 shadow-none focus:outline-none focus:placeholder:text-transparent'
            )}
            rows="4"
            placeholder="Want to share anything with our chefs? (optional)"
          />

          <button
            onClick={onSubmitReview}
            className="textMd btnPrimary btn w-full rounded py-4 text-white-100 transition-colors"
            disabled={submitDisabled}
          >
            Submit Feedback
          </button>
        </div>
      </div>
    </BodyModal>
  );
};

DeliveredMealReviewModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  mealId: PropTypes.string.isRequired,
  menuId: PropTypes.string.isRequired,
  existingReview: Types.review,
  onAddReview: PropTypes.func.isRequired,
  onUpdateReview: PropTypes.func.isRequired,
  onShowToast: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  ratingSubmittedEvent: PropTypes.string,
  reviewSubmittedEvent: PropTypes.string,
};

const mapDispatchToProps = {
  onAddReview: addReview,
  onUpdateReview: updateReview,
};

export default connect(null, mapDispatchToProps)(DeliveredMealReviewModal);
